import React, { useEffect, useState } from "react";
import { Select, Spin } from "antd";
import { useTranslation } from "react-i18next";
// REDUX
import {
  usePatchOwnActionFiringModeMutation,
  usePatchUserActionFiringModeMutation,
} from "../../redux/users/userAPI";
// AUTH
import GETJwtToken from "../../redux/authentication/authentication";
import { useMenuPermission } from "../../customHooks/useScopedPermission";
import { Permission } from "../../types/permissions";

/**
 * Whose setting this control edits.
 *
 * Not a cosmetic distinction. Editing your OWN firing mode and editing SOMEONE
 * ELSE'S are separate grants on the server, reached through different URIs, and
 * this is what decides which endpoint and which permission apply. A single
 * "user id" prop that happened to equal your own id would silently pick the
 * admin path for a self-edit.
 */
export type FiringModeTarget = { kind: "self" } | { kind: "user"; userId: string };

/** The user-level options. Unlike an organization, a user may say INHERIT. */
const USER_OPTIONS = [
  { value: "INHERIT", labelKey: "actionFiringMode.inherit" },
  { value: "TWO_STEP", labelKey: "actionFiringMode.twoStep" },
  { value: "ONE_CLICK", labelKey: "actionFiringMode.oneClick" },
];

const FALLBACK_LABEL: Record<string, string> = {
  INHERIT: "Use the organization's setting",
  TWO_STEP: "Two-step — pick a floor, then press the command",
  ONE_CLICK: "One-click — the floor button sends",
};

/**
 * Edits one user's action-firing-mode override.
 *
 * Saves through the DEDICATED endpoint rather than riding the general user
 * update, so the separate grant the server enforces is not quietly widened to
 * everyone who can edit a user at all.
 *
 * Renders nothing without the matching permission. Not a disabled control: an
 * operator who cannot change this does not benefit from seeing a greyed-out box
 * where a setting would be, and a disabled control invites a support ticket
 * that a missing one does not.
 */
export default function ActionFiringModeSelect({
  target,
  value,
  onSaved = undefined,
}: {
  target: FiringModeTarget;
  /** The user's own stored value, which may be INHERIT. */
  value: string | undefined;
  /** Called with the saved value, so the caller can update what it displays. */
  onSaved?: (saved: string) => void;
}) {
  const [t] = useTranslation("global");
  const [selected, setSelected] = useState<string>(value || "INHERIT");
  const [saving, setSaving] = useState(false);

  const [triggerPatchOwn] = usePatchOwnActionFiringModeMutation();
  const [triggerPatchUser] = usePatchUserActionFiringModeMutation();

  const isSelf = target.kind === "self";
  const permission = isSelf
    ? Permission.UsersActionFiringModeUpdate
    : Permission.UsersActionFiringModeUserUpdate;
  const allowed = useMenuPermission(permission);

  // A later fetch can land after this mounted -- the user drawer opens before
  // its own query resolves. Without this the control would keep showing the
  // placeholder value it was born with.
  useEffect(() => {
    setSelected(value || "INHERIT");
  }, [value]);

  if (!allowed) return null;

  const onChange = async (next: string) => {
    const previous = selected;
    setSelected(next);
    setSaving(true);

    try {
      const token = await GETJwtToken();
      if (isSelf) {
        await triggerPatchOwn({ actionFiringMode: next, token }).unwrap();
      } else {
        await triggerPatchUser({ id: target.userId, actionFiringMode: next, token }).unwrap();
      }
      onSaved?.(next);
    } catch {
      // Reverted rather than left showing the value that did not save. The
      // failure itself is reported by GlobalAlert2; what must not happen is the
      // control claiming a setting is in force when the server rejected it,
      // since the setting governs whether a floor press dispatches immediately.
      setSelected(previous);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <span className="generalStyles__inputLabel">
        {t("actionFiringMode.label", "Action Firing")}
      </span>
      <Select
        style={{ width: "100%", marginTop: 4 }}
        value={selected}
        disabled={saving}
        onChange={onChange}
        data-testid="user-action-firing-mode"
        options={USER_OPTIONS.map((o) => ({
          value: o.value,
          label: t(o.labelKey, FALLBACK_LABEL[o.value]),
        }))}
      />
      {saving && <Spin size="small" style={{ marginTop: 4 }} />}
      <span className="generalStyles__infoText">
        {t(
          "actionFiringMode.help",
          "Whether picking a floor sends the command immediately, or stages it for a second press.",
        )}
      </span>
    </div>
  );
}
